import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function ElectronConfigurationTool() {
  const [atomicNumber, setAtomicNumber] = useState("");

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: "Home", url: "https://calclab.app" },
      { name: "Chemistry", url: "https://calclab.app/chemistry" },
      { name: "Electron Configuration Tool", url: "https://calclab.app/calculator/electron-configuration" }
    ], lang),
    (lang) => calculatorToolSchema(
      "Electron Configuration Tool",
      "Generate electron configuration for any element. Free online chemistry tool using Aufbau principle.",
      "https://calclab.app/calculator/electron-configuration",
      lang
    )
  ]);
  const [result, setResult] = useState<string | null>(null);

  const getElectronConfiguration = (z: number): string => {
    const orbitals = [
      "1s", "2s", "2p", "3s", "3p", "4s", "3d", "4p", "5s", "4d", "5p", "6s", "4f", "5d", "6p", "7s", "5f", "6d", "7p"
    ];
    const maxElectrons = [2, 2, 6, 2, 6, 2, 10, 6, 2, 10, 6, 2, 14, 10, 6, 2, 14, 10, 6];
    
    let config = "";
    let remaining = z;
    
    for (let i = 0; i < orbitals.length && remaining > 0; i++) {
      const electrons = Math.min(remaining, maxElectrons[i]);
      config += orbitals[i] + (electrons > 1 ? `^${electrons}` : `^${electrons}`) + " ";
      remaining -= electrons;
    }
    
    return config.trim();
  };

  const calculate = () => {
    const z = parseInt(atomicNumber);

    if (isNaN(z) || z < 1 || z > 118) {
      toast({
        title: "Invalid Input",
        description: "Please enter an atomic number between 1 and 118.",
        variant: "destructive",
      });
      return;
    }

    const config = getElectronConfiguration(z);
    setResult(config);

    toast({
      title: "Configuration Generated",
      description: `Atomic number: ${z}`,
    });
  };

  const reset = () => {
    setAtomicNumber("");
    setResult(null);
  };

  return (
    <PageLayout 
      calculatorName="Electron Configuration Tool" 
      calculatorPath="/calculator/electron-configuration"
      category="chemistry"
      subcategory="Atomic Structure"
    >
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle>Electron Configuration Tool</CardTitle>
            <CardDescription>
              Generate electron configuration for any element
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="atomicNumber">Atomic Number (Z)</Label>
                <Input
                  id="atomicNumber"
                  type="number"
                  step="1"
                  value={atomicNumber}
                  onChange={(e) => setAtomicNumber(e.target.value)}
                  placeholder="Enter atomic number (1-118)"
                />
              </div>
            </div>

            <div className="flex gap-4">
              <Button onClick={calculate} className="flex-1">Generate</Button>
              <Button onClick={reset} variant="outline" className="flex-1">Reset</Button>
            </div>

            {result && (
              <Card className="bg-muted/50">
                <CardHeader>
                  <CardTitle className="text-lg">Electron Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-xl font-mono">{result}</p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-sm">Aufbau Principle</CardTitle>
              </CardHeader>
              <CardContent className="text-sm text-muted-foreground">
                <p>
                  Electrons fill orbitals in order of increasing energy:
                  1s → 2s → 2p → 3s → 3p → 4s → 3d → 4p → 5s...
                </p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
